<template>
  <div
    class="
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-border-sf-slate-lighter-1
      tw-bg-white
    "
  >
    <div
      class="
        tw-flex
        tw-items-center
        tw-justify-between
        tw-border-0
        tw-border-b
        tw-border-solid
        tw-border-sf-slate-lighter-1
        tw-px-6
        tw-py-4
      "
      data-testid="users-list-toolbar"
    >
      <UsersToolbar />
      <SfButtonDs
        variant="primary"
        icon-before="plus"
        data-testid="users-list-add"
        @click="userPanel.openNew(list === userLists.EXTERNAL)"
      >
        {{ I18n.t(ADD_BUTTON_KEYS[list]) }}
      </SfButtonDs>
    </div>
    <!-- This wrapper rounds the bottom corners of the table. It cannot move up
         to the card, which would cut off the filter menus that open past the
         toolbar. -->
    <div class="tw-overflow-hidden tw-rounded-b-lg">
      <div
        v-if="loading"
        class="tw-flex tw-flex-col tw-gap-4 tw-px-4 tw-py-6"
        data-testid="users-list-loading"
      >
        <SfSkeletonLoader
          v-for="placeholder in SKELETON_ROWS"
          :key="placeholder"
          width="100%"
          height="1.5rem"
        />
      </div>
      <div
        v-else-if="error"
        class="tw-px-4 tw-py-6 tw-text-sm tw-text-sf-slate-dark-1"
        data-testid="users-list-error"
      >
        {{ I18n.t('user.table.load_error') }}
      </div>
      <UsersNoResults v-else-if="users.length === 0 && isFiltered" />
      <EmptyState
        v-else-if="users.length === 0"
        :text="I18n.t(EMPTY_STATE_KEYS[list])"
      />
      <UsersTable
        v-else
        :list="list"
        :rows="users"
        :sort-by="sortBy"
        :sort-direction="sortDirection"
        @open="userPanel.open($event)"
        @sort="setSort"
      />
      <div
        v-if="totalPages > 1"
        class="
          tw-border-x-0
          tw-border-b-0
          tw-border-t
          tw-border-solid
          tw-border-sf-slate-lighter-1
          tw-py-5
        "
        data-testid="users-list-pagination"
      >
        <SfPaginationDs
          :current-page="page"
          :total-pages="totalPages"
          @change-page="setPage"
        />
      </div>
    </div>

    <UserDetailPanel @refresh="usersList.loadUsers()" />
  </div>
</template>

<script setup lang="ts">
import type { UserList } from '@app/application/user-management/types'

import { storeToRefs } from 'pinia'

import {
  ADD_BUTTON_KEYS,
  EMPTY_STATE_KEYS,
  SKELETON_ROWS,
} from '@app/application/user-management/constants'
import { userLists } from '@app/application/user-management/types'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import { useUsersListStore } from '@app/application/user-management/stores/users-list'
import useI18n from '@app/shared/vue/composables/use-I18n'
import EmptyState from '@app/shared/vue/components/molecules/empty-state.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfPaginationDs from '@app/shared/vue/components/sf-pagination/sf-pagination-ds.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import UserDetailPanel from './user-panel/user-detail-panel.vue'
import UsersNoResults from './list/users-no-results.vue'
import UsersToolbar from './list/users-toolbar.vue'
import UsersTable from './users-table.vue'

defineOptions({ name: 'UsersList' })

interface Props {
  list: UserList
}

const { list } = defineProps<Props>()

const I18n = useI18n()
const usersList = useUsersListStore()
const userPanel = useUserPanelStore()

const {
  users,
  totalPages,
  loading,
  error,
  sortBy,
  sortDirection,
  page,
  isFiltered,
} = storeToRefs(usersList)
const { setSort, setPage } = usersList

usersList.initialize(list)
</script>
